/* eslint-disable max-len */
import React, { useState, useEffect, useContext, useRef } from "react";
import GlobalContext from "../../components/Context/GloablContext";
import { dataService } from "../../services/data.services";
import AddHoliday from "./AddHoliday";
import FeeStructureDropdown from "../../components/FeeStructureDropdown";
import Toast from "../../components/Toast";
import Loader from "../../components/Loader";
import { GetBranchId } from "../../CommonMethods/GetBranchId";
import ItemShimmer from "../../components/Shimmer/itemShimmer";
import {
    formatDateLabel,
    formatDayLabel,
    getHolidayTypeLabel,
    isAcademicYearCompleted,
    isHolidayPast,
    normalizeHolidayItems,
} from "./CommonMethods";

export default function EmployeeHoliday() {
    const [branchId, setBranchId] = useState(null);
    const [academicYears, setAcademicYears] = useState([]);
    const [selectedAcademicYear, setSelectedAcademicYear] = useState(null);
    const [holidays, setHolidays] = useState([]);
    const [fetching, setFetching] = useState(false);
    const [academicYearLoading, setAcademicYearLoading] = useState(false);

    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedHolidayData, setSelectedHolidayData] = useState(null);
    const [modalInitialScreen, setModalInitialScreen] = useState("view");
    const [modalOpen, setModalOpen] = useState(false);
    const [deleteConfirmationMessage, setDeleteConfirmationMessage] = useState("");

    const [toastErrorMessage, setToastErrorMessage] = useState("");
    const [toastSuccessMessage, setToastSuccessMessage] = useState("");
    const [openActionMenuKey, setOpenActionMenuKey] = useState(null);

    const { isInactiveBranch } = useContext(GlobalContext);
    const initialFetchDoneRef = useRef(false);
    const actionMenuRef = useRef(null);
    const actionTriggerRef = useRef(null);

    const onAcademicYearChange = (ay) => {
        setSelectedAcademicYear(ay || null);
    };

    const getAcademicYears = async () => {
        if (!branchId) return;
        try {
            setAcademicYearLoading(true);
            const endpoint = `academic-years?branch_id=${branchId}&filter=true&status=ongoing,upcoming,completed`;
            const response = await dataService.GetAPI(endpoint);
            const temp = response?.data?.data?.data || [];
            setAcademicYears(temp);

            if (!selectedAcademicYear) {
                const ongoing = temp.find((ay) => ay?.status === "ongoing");
                if (ongoing) {
                    setSelectedAcademicYear(ongoing);
                } else if (temp.length > 0) {
                    setSelectedAcademicYear(temp[0]);
                } else {
                    setSelectedAcademicYear(null);
                }
            }
        } catch (error) {
            console.error("Error fetching academic years:", error);
            setToastErrorMessage(
                error?.response?.data?.message || "Failed to load academic years"
            );
        } finally {
            setAcademicYearLoading(false);
        }
    };

    const getHolidayScopeLabel = (item) => {
        const scope = String(item?.holiday?.holiday_scope || "").toUpperCase();
        const scopeLabels = {
            BRANCH: "Branch",
            STAFF: "Staff",
        };
        return scopeLabels[scope] || "-";
    };
    const employeeHolidayRows = [...(holidays || [])]
        .sort((a, b) =>
            (a?.start_date || a?.date || "").localeCompare(
                b?.start_date || b?.date || ""
            )
        );

    const getHolidays = async () => {
        if (!selectedAcademicYear?._id) {
            setToastErrorMessage("Please select Academic Year");
            return;
        }

        try {
            setFetching(true);
            const params = new URLSearchParams({
                branch_id: branchId,
                academic_year_id: selectedAcademicYear._id,
            });

            const endpoint = `holidays/list-holidays?${params.toString()}&is_employee_holiday=true`;
            const response = await dataService.GetAPI(endpoint, true);

            if (response?.data?.status === 200) {
                setHolidays(normalizeHolidayItems(response?.data?.data || {}, {
                    idKeys: ["employee_holiday_id", "branch_holiday_id", "holiday_range_id"],
                }));
            } else {
                setHolidays([]);
            }
        } catch (error) {
            console.error("Error fetching employee holidays:", error);
            setHolidays([]);
            setToastErrorMessage(
                error?.response?.data?.message || "Failed to fetch holidays"
            );
        } finally {
            setFetching(false);
        }
    };

    const fetchHolidayDetails = async (item) => {
        const holiday = item?.holiday;
        if (!holiday || !selectedAcademicYear?._id || !branchId) return null;

        const params = new URLSearchParams({
            branch_id: branchId,
            academic_year_id: selectedAcademicYear._id,
        });

        if (holiday?.holiday_range_id) {
            params.append("holiday_range_id", holiday.holiday_range_id);
        } else if (holiday?.employee_holiday_id) {
            params.append("employee_holiday_id", holiday.employee_holiday_id);
        } else if (holiday?.branch_holiday_id) {
            params.append("branch_holiday_id", holiday.branch_holiday_id);
        } else {
            return null;
        }

        const endpoint = `holidays/view?${params.toString()}`;
        const response = await dataService.GetAPI(endpoint, true);
        if (response?.data?.status !== 200) return null;

        const holidayData = response?.data?.data?.holiday || null;
        if (!holidayData) return null;

        holidayData.holiday_range_id = holiday?.holiday_range_id || holidayData?.holiday_range_id || null;
        holidayData.employee_holiday_id = holiday?.employee_holiday_id || holidayData?.employee_holiday_id || null;
        holidayData.branch_holiday_id = holiday?.branch_holiday_id || holidayData?.branch_holiday_id || null;
        return holidayData;
    };

    const buildHolidayDataFromItem = (item) => {
        const holiday = item?.holiday;
        if (!holiday) return null;

        return {
            ...holiday,
            start_date: item?.start_date || item?.date || holiday?.start_date || null,
            end_date: item?.end_date || item?.date || holiday?.end_date || null,
            holiday_range_id: holiday?.holiday_range_id || item?.date_range_id || null,
            employee_holiday_id: holiday?.employee_holiday_id || null,
            branch_holiday_id: holiday?.branch_holiday_id || null,
        };
    };

    const openHolidayModal = (item, screen) => {
        if (screen !== "view" && isAcademicYearCompleted(selectedAcademicYear)) {
            setToastErrorMessage("AY Completed. Hence Holidays cannot be modified");
            return;
        }
        if (screen === "delete" && isHolidayPast(item)) {
            setToastErrorMessage("Past holiday cannot be deleted.");
            return;
        }

        const holidayData = buildHolidayDataFromItem(item);
        if (!holidayData) {
            setToastErrorMessage("Failed to load holiday details");
            return;
        }

        setSelectedDate(item?.date || item?.start_date || null);
        setSelectedHolidayData(holidayData);
        setDeleteConfirmationMessage("");
        setModalInitialScreen(screen);
        setModalOpen(true);
    };

    const handleExcludeHolidayForEmployee = (item) => {
        if (isAcademicYearCompleted(selectedAcademicYear)) {
            setToastErrorMessage("AY Completed, Holidays cannot be added");
            return;
        }
        if (isHolidayPast(item)) {
            setToastErrorMessage("Past Holiday. Holiday cannot be excluded for employees.");
            return;
        }
        const holidayData = buildHolidayDataFromItem(item);
        if (!holidayData) {
            setToastErrorMessage("Failed to load holiday details");
            return;
        }
        setSelectedDate(item?.date || item?.start_date || null);
        setSelectedHolidayData(holidayData);
        setDeleteConfirmationMessage("Do you want to exclude this holiday for employees?");
        setModalInitialScreen("delete");
        setModalOpen(true);
    };

    const handleAddHolidayButton = () => {
        if (!selectedAcademicYear) {
            setToastErrorMessage("Please select Academic Year");
            return;
        }
        if (isAcademicYearCompleted(selectedAcademicYear)) {
            setToastErrorMessage("AY Completed, Holidays cannot be added");
            return;
        }
        setSelectedDate(null);
        setSelectedHolidayData(null);
        setDeleteConfirmationMessage("");
        setModalInitialScreen("view");
        setModalOpen(true);
    };

    useEffect(() => {
        let mounted = true;
        GetBranchId()
            .then((id) => {
                if (mounted) setBranchId(id);
            })
            .catch((err) => {
                console.error("Failed to get branch id", err);
                if (mounted) setToastErrorMessage("Failed to determine branch");
            });
        return () => {
            mounted = false;
        };
    }, []);

    useEffect(() => {
        if (branchId) {
            getAcademicYears();
        }
    }, [branchId]);

    useEffect(() => {
        if (selectedAcademicYear?._id && !initialFetchDoneRef.current) {
            initialFetchDoneRef.current = true;
            getHolidays();
        }
    }, [selectedAcademicYear?._id]);

    const closeActionMenu = () => {
        setOpenActionMenuKey(null);
        if (actionTriggerRef.current) {
            actionTriggerRef.current.focus();
        }
    };

    useEffect(() => {
        if (!openActionMenuKey) return;

        const handleOutside = (event) => {
            const clickedOutsideActionMenu = (
                actionMenuRef.current &&
                !actionMenuRef.current.contains(event.target) &&
                actionTriggerRef.current &&
                !actionTriggerRef.current.contains(event.target)
            );

            if (clickedOutsideActionMenu) {
                closeActionMenu();
            }
        };

        const handleEsc = (event) => {
            if (event.key === "Escape") {
                closeActionMenu();
            }
        };

        document.addEventListener("mousedown", handleOutside);
        document.addEventListener("keydown", handleEsc);
        return () => {
            document.removeEventListener("mousedown", handleOutside);
            document.removeEventListener("keydown", handleEsc);
        };
    }, [openActionMenuKey]);

    useEffect(() => {
        closeActionMenu();
    }, [selectedAcademicYear?._id]);

    const handleActionMenuClick = (key) => (event) => {
        event.stopPropagation();
        if (openActionMenuKey === key) {
            closeActionMenu();
            return;
        }
        actionTriggerRef.current = event.currentTarget;
        setOpenActionMenuKey(key);
    };

    return (
        <div className="bg-white">
            <div className="my-2 grid grid-cols-1 items-end gap-4 rounded border p-3 md:grid-cols-3  sticky top-0  z-20 bg-white">
                <div className="flex w-[250px] flex-col">
                    <label className="mb-1 inline-flex text-f7 font-normal text-[#031624]">
                        Academic Year<span className="req-fld-astrk">*</span>
                    </label>
                    <FeeStructureDropdown
                        dropDownName={"academic_year"}
                        id={"name"}
                        test_id={"year_name"}
                        value={selectedAcademicYear?.name || "Select Academic Year"}
                        placeHolder={"Select Academic Year"}
                        isMandatoryField={true}
                        options={academicYears}
                        handleOnchangeInput={(key, value) => onAcademicYearChange(value)}
                        loading={academicYearLoading}
                        buttonDisabled={academicYearLoading}
                    />
                </div>

                <div className="flex justify-end gap-3 md:col-span-2">
                    <button
                        type="button"
                        onClick={getHolidays}
                        style={{'min-width': '120px'}}
                        disabled={academicYearLoading || fetching}
                        className={`view-actn-btn`}
                    >
                        {academicYearLoading || fetching ? <Loader /> : "Apply Filter"}
                    </button>

                    {!isInactiveBranch && (
                        <button
                            type="button"
                            onClick={handleAddHolidayButton}
                            className="prmy-btn onboardInstitution"
                        >
                            Add Holiday(s)
                        </button>
                    )}
                </div>
            </div>

            <div className="mt-4">
                {fetching ? (
                    <ItemShimmer classkey={""} />
                ) : (
                    <div className={`flex mb-3 overflow-auto ${holidays.length < 10 ? 'pb-[62px]' : 'pb-[6px]'} scroll-bar px-1`} id="search-result" style={{ maxHeight: 'calc(100vh - 250px)' }}>
                        <table className="w-full" id="admin-table" class="stickyhead">
                            <thead>
                                <tr>
                                    <th className="w-[16%] text-left">Date</th>
                                    <th className="w-[16%] text-left">Day</th>
                                    <th className="w-[30%] text-left">Name</th>
                                    <th className="w-[15%] text-left">Holiday Type</th>
                                    <th className="w-[18%] text-left">Holiday Scope</th>
                                    <th className="w-[20%] text-left">Actions</th>
                                </tr>
                            </thead>
                            {employeeHolidayRows.length === 0 ? (
                                <tbody>
                                    <tr>
                                        <td colSpan={6} className="no-records-message" id="no-matching-result" style={{ lineHeight: "100px" }}>
                                            No records found.
                                        </td>
                                    </tr>
                                </tbody>
                            ) : (
                                <tbody>
                                    {employeeHolidayRows.map((item) => {
                                        const rowKey = item?.id || item?.date || `${item?.start_date}-${item?.end_date}`;
                                        const holidayScope = String(item?.holiday?.holiday_scope || "").toUpperCase();

                                        return (
                                            <tr key={rowKey} className="border-t">
                                                <td className="px-4 py-2">{formatDateLabel(item)}</td>
                                                <td className="px-4 py-2">{formatDayLabel(item)}</td>
                                                <td className="px-4 py-2">{item?.holiday?.name || "-"}</td>
                                                <td className="px-4 py-2">{getHolidayTypeLabel(item)}</td>
                                                <td className="px-4 py-2">{getHolidayScopeLabel(item)}</td>
                                                <td className="px-4 py-2">
                                                    <div className="relative flex items-center justify-center prevent-click" data-te-dropdown-position="dropstart">
                                                        <button
                                                            type="button"
                                                            disabled={isInactiveBranch}
                                                            className={`list-more-btn prevent-click ${isInactiveBranch?"img-disable":""}`}
                                                            onClick={handleActionMenuClick(rowKey)}
                                                            aria-haspopup="menu"
                                                            aria-expanded={openActionMenuKey === rowKey}
                                                        >
                                                            <img src="/images/listAction.svg" alt="Actions" />
                                                        </button>
                                                        {openActionMenuKey === rowKey && (
                                                            <ul
                                                                ref={actionMenuRef}
                                                                className="list-more-optns-div [&[data-te-dropdown-show]]:block prevent-click"
                                                                role="menu"
                                                            >
                                                                {holidayScope === "BRANCH" && (
                                                                    <li>
                                                                        <button
                                                                            type="button"
                                                                            className="list-more-optns prevent-click"
                                                                            onClick={() => {
                                                                                closeActionMenu();
                                                                                handleExcludeHolidayForEmployee(item);
                                                                            }}
                                                                            role="menuitem"
                                                                        >
                                                                            Exclude Holiday for Employees
                                                                        </button>
                                                                    </li>
                                                                )}
                                                                {holidayScope === "STAFF" && (
                                                                    <>
                                                                        <li>
                                                                            <button
                                                                                type="button"
                                                                                className="list-more-optns prevent-click"
                                                                                onClick={() => {
                                                                                    closeActionMenu();
                                                                                    openHolidayModal(item, "edit");
                                                                                }}
                                                                                role="menuitem"
                                                                            >
                                                                                Edit Holiday
                                                                            </button>
                                                                        </li>
                                                                        <li>
                                                                            <button
                                                                                type="button"
                                                                                className="list-more-optns prevent-click"
                                                                                onClick={() => {
                                                                                    closeActionMenu();
                                                                                    openHolidayModal(item, "delete");
                                                                                }}
                                                                                role="menuitem"
                                                                            >
                                                                                Delete Holiday
                                                                            </button>
                                                                        </li>
                                                                    </>
                                                                )}
                                                            </ul>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            )}
                        </table>
                    </div>
                )}
            </div>

            <AddHoliday
                academicYear={selectedAcademicYear}
                holidayScope="EMPLOYEE"
                setToastmessage={setToastSuccessMessage}
                setToastErrorMessage={setToastErrorMessage}
                open={modalOpen}
                branch_id={branchId}
                onClose={() => {
                    setModalOpen(false);
                    setSelectedDate(null);
                    setSelectedHolidayData(null);
                    setDeleteConfirmationMessage("");
                    setModalInitialScreen("view");
                }}
                getHolidays={getHolidays}
                selectedDate={selectedDate}
                holidayData={selectedHolidayData}
                initialEditModeScreen={modalInitialScreen}
                deleteConfirmationMessage={deleteConfirmationMessage}
            />

            {!modalOpen && toastErrorMessage !== "" && (
                <Toast
                    message={toastErrorMessage}
                    type="error"
                    setToastmessage={setToastErrorMessage}
                />
            )}
            {!modalOpen && toastSuccessMessage !== "" && (
                <Toast
                    message={toastSuccessMessage}
                    type="success"
                    setToastmessage={setToastSuccessMessage}
                />
            )}
        </div>
    );
}
